/**
 * Felix Newsletter Embed — <felix-newsletter-embed>
 *
 * Sizes the issue iframe to its content height. The framed document is always our
 * own stored copy of the sent email, served from the issue's own URL, so it is
 * same-origin and can be measured directly — no handshake, no reporter script in
 * the document, and nothing for the email itself to participate in.
 *
 * The server renders a min-height floor so the block occupies roughly the right
 * space before the first measurement lands.
 *
 * @package Felix
 */

// Taller than any real newsletter; a guard against a pathological document
// stretching the page rather than a value anything should reach.
const MAX_HEIGHT = 60000

class FelixNewsletterEmbed extends HTMLElement {
	connectedCallback() {
		this.frame = this.querySelector('iframe')
		if (!this.frame) return

		this.lastHeight = 0

		this.setHeight = (h) => {
			// Bail on an unchanged height: measure() runs from a ResizeObserver on the
			// framed document, whose scrollHeight depends on the height we set here.
			// Without this the two can chase each other.
			if (h === this.lastHeight) return
			this.lastHeight = h

			const svg = this.querySelector('svg')
			const fo = this.querySelector('foreignObject')
			const w = svg ? (svg.getAttribute('viewBox') || '0 0 575 0').split(' ')[2] : '575'
			if (svg) svg.setAttribute('viewBox', `0 0 ${w} ${h}`)
			if (fo) fo.setAttribute('height', String(h))
			this.frame.setAttribute('height', String(h))
		}

		this.measure = () => {
			let doc = null
			try {
				doc = this.frame.contentDocument
			} catch {
				doc = null
			}
			// Before load (and with loading="lazy" that is the common first pass) the
			// frame holds an empty about:blank whose height would collapse the block.
			if (!doc || !doc.documentElement) return false
			if (doc.readyState === 'loading') return false
			if (!doc.body || !doc.body.childElementCount) return false

			const height = Math.ceil(doc.documentElement.scrollHeight)
			if (!Number.isFinite(height) || height <= 0) return false
			this.setHeight(Math.min(height, MAX_HEIGHT))

			if (!this.observer && typeof ResizeObserver !== 'undefined') {
				this.observer = new ResizeObserver(() => this.measure())
				this.observer.observe(doc.documentElement)
			}
			return true
		}

		// Images inside the document keep loading after its own load event, so the
		// ResizeObserver established above is what settles the final height.
		this.frame.addEventListener('load', this.measure)
		this.measure()
	}

	disconnectedCallback() {
		this.frame?.removeEventListener('load', this.measure)
		this.observer?.disconnect()
		this.observer = null
	}
}

if (!customElements.get('felix-newsletter-embed')) {
	customElements.define('felix-newsletter-embed', FelixNewsletterEmbed)
}
